import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { User } from '../../models/user';
import { AssignedCategory } from '../../models/assignedCategory';
import { Table } from '../../models/table';
import { SharedModule } from '../../shared/shared.module';
import { MaterialModule } from '../../material/material.module';
import { TableComponent } from '../../shared/table/table.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FormLinkDialogComponent } from '../../shared/form-link-dialog/form-link-dialog.component';
import _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [
    SharedModule,
    TableComponent,
    MaterialModule
  ],
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss'
})
export class ClientsComponent implements OnInit, OnChanges {

  clientsTable: Table<any>;
  selectedContentCreators: User[] = [];
  assignedCategories: AssignedCategory[] = [];
  searchTerm: string = '';
  loaded: boolean = false;

  private overlay = inject(Overlay);
  private vcr = inject(ViewContainerRef);
  private dialog = inject(MatDialog);

  overlayRef!: OverlayRef;
  overlayRefForInstructor!: OverlayRef;

  @Input() contentCreators: User[] = [];
  @Input() previouslySelectedContentCreators: User[] = [];
  @Input() onlyTable: boolean = false;

  @ViewChild('editInstructorsTemplate', { static: true }) editInstructorsTemplate: TemplateRef<any>;
  @ViewChild('editFormLinkTemplate', { static: true }) editFormLinkTemplate: TemplateRef<any>;
  @ViewChild('instructorOverlay') instructorOverlay: TemplateRef<any>;
  @ViewChild('languageOverlay') languageOverlay!: TemplateRef<any>;

  @Output() checkSectionCompletionEv = new EventEmitter<boolean>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() selectedContentCreatorsChange = new EventEmitter<User[]>();

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['previouslySelectedContentCreators'] && !changes['previouslySelectedContentCreators'].firstChange) {
      if (this.previouslySelectedContentCreators?.length) {
        this.selectedContentCreators = this.previouslySelectedContentCreators;
      }
    }

    if (changes['contentCreators'] && !changes['contentCreators'].firstChange && !this.loaded) {
      this.initializeClientsTable();
      this.loaded = true;
    }
  }

  ngOnInit(): void {
    if (this.previouslySelectedContentCreators) {
      this.selectedContentCreators = this.previouslySelectedContentCreators;
    }

    this.initializeClientsTable();
  }

  initializeClientsTable() {
    const columns = [
      { label: 'general.id', field: 'id' },
      { label: 'general.company_name', field: 'name' },
      { label: 'general.instructor', field: 'instructor', template: this.editInstructorsTemplate, showOnlyTemplate: true },
      { label: 'general.form', field: 'form_link', template: this.editFormLinkTemplate, showOnlyTemplate: true },
    ];

    const commonOptions = {
      columns: columns,
      selectable: false,
      actions: true
    };

    this.clientsTable = new Table({
      ...commonOptions,
      deleteBtn: true,
    });
  }

  updateContentCreators() {
    this.selectedContentCreatorsChange.emit(this.selectedContentCreators);
  }

  onSearchTermChange(term: string): void {
    this.searchTermChange.emit(term);
  }

  removeCreator(creator: User): void {
    this.selectedContentCreators = this.selectedContentCreators?.filter(c => c !== creator);
    this.updateContentCreators();
  }

  checkSectionCompletion() {
    this.checkSectionCompletionEv.emit(true)
  }

  selectCreator(event: MatSelectionListChange) {
    const selectedCreators = event.source.selectedOptions.selected.map(opt => opt.value as User);
    this.selectedContentCreators = selectedCreators;

    for (const creator of selectedCreators) {
      let assignedCategory = this.assignedCategories.find(cat => cat.userId === creator.id);

      if (!assignedCategory) {
        assignedCategory = new AssignedCategory({
          userId: creator.id,
          name: creator.username,
          nameLowercase: creator.username?.toLowerCase(),
          status: PublishStatusEnum.DRAFT
        });
        this.assignedCategories.push(assignedCategory);
      }

      creator.__assignedCategories.push(assignedCategory);
    }

    this.assignedCategories = this.assignedCategories.filter(cat =>
      selectedCreators.some(creator => creator.id === cat.userId)
    );

    for (const creator of this.contentCreators ?? []) {
      if (!selectedCreators.includes(creator)) {
        delete (creator as any).__assignedCategory;
      }
    }

    this.overlayRef?.updatePosition();
    this.updateContentCreators();
  }

  openOverlay(event: MouseEvent) {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(event.target as HTMLElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });

    const portal = new TemplatePortal(this.languageOverlay, this.vcr);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => this.overlayRef?.dispose());
  }

  isCreatorSelected(creator: User): boolean {
    return this.selectedContentCreators?.some(c => c.id === creator.id);
  }

  openInstructorOverlay(event: MouseEvent, user: any) {
    if (this.overlayRefForInstructor) this.overlayRefForInstructor.dispose();

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(event.target as HTMLElement)
      .withPositions([{
          originX: 'center',  
        originY: 'bottom',
        overlayX: 'center',   
        overlayY: 'top',
        offsetY: 8             
      }])
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRefForInstructor = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });

    const portal = new TemplatePortal(this.instructorOverlay, this.vcr, {
      assignedCategory: user.__assignedCategories[0],
      element: user
    });

    this.overlayRefForInstructor.attach(portal);
    this.overlayRefForInstructor.backdropClick().subscribe(() => this.overlayRefForInstructor?.dispose());
  }

  openFormlinkModal(assignedCategory: AssignedCategory) {
    const previousFormLink = assignedCategory.formLink;
    const previousRequireFormFirst = assignedCategory.requireFormFirst;

    const dialogRef = this.dialog.open(FormLinkDialogComponent, {
      minWidth: '50vw',
      height: '340px',
      data: {
        assignedCategory: _.cloneDeep(assignedCategory),
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        Object.assign(assignedCategory, result.assignedCategory);

        if (assignedCategory.formLink !== previousFormLink || assignedCategory.requireFormFirst !== previousRequireFormFirst) {
          assignedCategory.__modified = true;
        }
      }
    });
  }

  selectInstructor(event: MatSelectionListChange, assignedCategory: AssignedCategory) {
    const selected = event.source.selectedOptions.selected[0]?.value;
    if (selected && assignedCategory) {
      if (assignedCategory.instructor !== selected) {
        assignedCategory.instructor = selected;
        assignedCategory.__modified = true;
      }

      this.overlayRefForInstructor?.dispose();
    }
  }


}
